import { useCallback, useEffect, useRef, useState } from 'react';

interface UseFloatingPanelOptions {
  isOpen: boolean;      // quando passa de true para false, largura/altura voltam ao padrão
  initialWidth: number;
  initialHeight: number;
  minWidth?: number;
  minHeight?: number;
  rightMargin?: number; // margem mínima em relação à borda direita da janela ao redimensionar a largura
  topMargin?: number;   // margem mínima em relação ao topo da janela ao redimensionar a altura
}

interface UseFloatingPanelResult {
  panelRef: React.RefObject<HTMLDivElement>;
  width: number;
  height: number;
  dragOffset: { x: number; y: number };
  hasMoved: boolean;
  handleTopResize: (e: React.MouseEvent) => void;
  handleLeftResize: (e: React.MouseEvent) => void;
  handleCornerResize: (e: React.MouseEvent) => void;
  handleDragStart: (e: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * Torna um painel flutuante (fixed, ancorado por bottom/right) redimensionável
 * pelas bordas e arrastável pelo cabeçalho — sem alterar a posição/tamanho
 * padrão até que o usuário interaja.
 *
 * Arrastar é ignorado quando o clique começa em um elemento interativo
 * (button, a, input, textarea, select) dentro do cabeçalho, para não
 * atrapalhar os próprios controles do cabeçalho.
 */
export function useFloatingPanel({
  isOpen,
  initialWidth,
  initialHeight,
  minWidth = 300,
  minHeight = 300,
  rightMargin = 40,
  topMargin = 160,
}: UseFloatingPanelOptions): UseFloatingPanelResult {
  const [width, setWidth] = useState(initialWidth);
  const [height, setHeight] = useState(initialHeight);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Ao fechar, o tamanho volta ao padrão (a posição arrastada é preservada de propósito)
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      setWidth(initialWidth);
      setHeight(initialHeight);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialWidth, initialHeight]);

  const handleTopResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = height;
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      setHeight(Math.min(Math.max(startH + startY - ev.clientY, minHeight), window.innerHeight - topMargin));
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [height, minHeight, topMargin]);

  const handleLeftResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      setWidth(Math.min(Math.max(startW + startX - ev.clientX, minWidth), window.innerWidth - rightMargin));
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [width, minWidth, rightMargin]);

  const handleCornerResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = width;
    const startH = height;
    document.body.style.cursor = 'nwse-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      setWidth(Math.min(Math.max(startW + startX - ev.clientX, minWidth), window.innerWidth - rightMargin));
      setHeight(Math.min(Math.max(startH + startY - ev.clientY, minHeight), window.innerHeight - topMargin));
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [width, height, minWidth, minHeight, rightMargin, topMargin]);

  const handleDragStart = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, textarea, select, [role="button"]')) return;
    e.preventDefault();
    const startMouseX = e.clientX;
    const startMouseY = e.clientY;
    const startOffset = dragOffset;
    const rect = panelRef.current?.getBoundingClientRect();
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      let deltaX = ev.clientX - startMouseX;
      let deltaY = ev.clientY - startMouseY;
      if (rect) {
        const minVisible = 80;
        const nextLeft = rect.left + deltaX;
        const nextTop = rect.top + deltaY;
        const clampedLeft = Math.min(Math.max(nextLeft, -(rect.width - minVisible)), window.innerWidth - minVisible);
        const clampedTop = Math.min(Math.max(nextTop, 0), window.innerHeight - 48);
        deltaX = clampedLeft - rect.left;
        deltaY = clampedTop - rect.top;
      }
      setDragOffset({ x: startOffset.x + deltaX, y: startOffset.y + deltaY });
      setHasMoved(true);
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [dragOffset]);

  return { panelRef, width, height, dragOffset, hasMoved, handleTopResize, handleLeftResize, handleCornerResize, handleDragStart };
}
